import React, { useEffect, useState } from "react";
import { WeightPullWeightClasses } from "@eagerdog/constants";
import { IEventSummary, IShow, IWeightPullClassSetup } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./WeightSetupTab.module.scss";

interface IClassRow {
  starting_weight: string;
  block_weight: string;
  block_drop: string;
}

interface IProps {
  event: IEventSummary;
  show: IShow;
  onUpdate(updatedShow: IShow): void;
}

const DECIMAL_RE = /^\d*\.?\d*$/;
const INTEGER_RE = /^\d*$/;

const emptyRow = (): IClassRow => ({
  starting_weight: "",
  block_weight: "",
  block_drop: "",
});

const rowFromSetup = (setup?: IWeightPullClassSetup): IClassRow => ({
  starting_weight: setup?.starting_weight !== undefined ? String(setup.starting_weight) : "",
  block_weight: setup?.block_weight !== undefined ? String(setup.block_weight) : "",
  block_drop: setup?.block_drop !== undefined ? String(setup.block_drop) : "",
});

const rowsFromShow = (show: IShow): Record<string, IClassRow> => {
  const initial: Record<string, IClassRow> = {};
  for (const weightClass of WeightPullWeightClasses) {
    const existing = show.weight_pull_setups?.find(
      (s) => s.weight_class === weightClass.label
    );
    initial[weightClass.label] = rowFromSetup(existing);
  }
  return initial;
};

const parseOptionalNumber = (value: string): number | undefined => {
  if (value.trim() === "") {
    return undefined;
  }
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseOptionalInt = (value: string): number | undefined => {
  if (value.trim() === "") {
    return undefined;
  }
  const parsed = parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : undefined;
};

const WeightSetupTab: React.FC<IProps> = ({ event, show, onUpdate }) => {
  const [rows, setRows] = useState<Record<string, IClassRow>>(() => rowsFromShow(show));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(rowsFromShow(show));
  }, [show._id]);

  useEffect(() => {
    if (show.weight_pull_setups?.length) {
      setRows(rowsFromShow(show));
    }
  }, [show.weight_pull_setups]);

  const updateField = (
    weightClass: string,
    field: keyof IClassRow,
    value: string,
    pattern: RegExp
  ) => {
    if (value !== "" && !pattern.test(value)) {
      return;
    }
    setRows((prev) => ({
      ...prev,
      [weightClass]: {
        ...(prev[weightClass] || emptyRow()),
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    const weight_pull_setups: IWeightPullClassSetup[] = WeightPullWeightClasses
      .map((weightClass) => {
        const row = rows[weightClass.label] || emptyRow();
        const setup: IWeightPullClassSetup = {
          weight_class: weightClass.label,
          starting_weight: parseOptionalNumber(row.starting_weight),
          block_weight: parseOptionalNumber(row.block_weight),
          block_drop: parseOptionalInt(row.block_drop),
        };
        return setup;
      })
      .filter((setup) =>
        setup.starting_weight !== undefined ||
        setup.block_weight !== undefined ||
        setup.block_drop !== undefined
      );

    setSaving(true);
    apiService.updateShow(event._id, show._id, {
      ...show,
      weight_pull_setups,
    }).then((savedShow) => {
      const merged: IShow = {
        ...savedShow,
        weight_pull_setups: savedShow.weight_pull_setups?.length
          ? savedShow.weight_pull_setups
          : weight_pull_setups,
      };
      setRows(rowsFromShow(merged));
      toast.show({
        title: "Weight Setup",
        content: "Weight setup has been saved",
        duration: 10000,
        type: "success",
      });
      onUpdate(merged);
    }).catch(() => {
      toast.show({
        title: "Weight Setup",
        content: "Failed to save weight setup",
        duration: 10000,
        type: "fail",
      });
    }).finally(() => {
      setSaving(false);
    });
  };

  return (
    <div className={styles.WeightSetupTab}>
      <p>
        Set the cart / starting weight, block weight, and block drop for each weight class.
        Block drop is how many blocks are added for each pull. These values fill the increment
        columns on the judge book.
      </p>
      <div className={styles.tableWrap}>
        <table className={styles.setupTable}>
          <thead>
            <tr>
              <th>Weight Class</th>
              <th>Cart or Starting Weight</th>
              <th>Block Weight</th>
              <th>Block Drop</th>
            </tr>
          </thead>
          <tbody>
            {WeightPullWeightClasses.map((weightClass) => {
              const row = rows[weightClass.label] || emptyRow();
              return (
                <tr key={weightClass.label}>
                  <td className={styles.classLabel}>{weightClass.label}</td>
                  <td>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="lbs"
                      value={row.starting_weight}
                      onChange={(e) => updateField(weightClass.label, "starting_weight", e.target.value, DECIMAL_RE)}
                    />
                  </td>
                  <td>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="lbs"
                      value={row.block_weight}
                      onChange={(e) => updateField(weightClass.label, "block_weight", e.target.value, DECIMAL_RE)}
                    />
                  </td>
                  <td>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="blocks"
                      value={row.block_drop}
                      onChange={(e) => updateField(weightClass.label, "block_drop", e.target.value, INTEGER_RE)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={styles.actions}>
        <Button isLoading={saving} onClick={handleSave}>Save</Button>
      </div>
    </div>
  );
};

export default WeightSetupTab;
